import { Button, Card, Chip, faDigits, formatTime, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { accountingApi, type AccountingBackfillResult } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

const ROLE_LABELS: Record<string, string> = {
  receivable: "حساب‌های دریافتنی",
  revenue: "درآمد فروش",
  cash: "صندوق / وجه نقد",
  bank: "بانک",
  "cheques-in-hand": "اسناد دریافتنی / چک‌های در دست وصول",
  "vat-payable": "مالیات بر ارزش افزوده پرداختنی",
  payable: "حساب‌های پرداختنی تجاری",
  purchases: "خرید و موجودی مواد اولیه",
  "cheques-payable": "اسناد پرداختنی تجاری",
  "year-result": "سود و زیان انباشته",
  "salaries-expense": "هزینه حقوق و دستمزد پرسنل (۵۱۴)",
  "salaries-payable": "حقوق و دستمزد پرداختنی پرسنل (۲۱۴)",
  "payroll-deductions-payable": "بیمه و مالیات پرداختنی حقوق (۲۱۵)",
};

export function SettingsTab() {
  const [retryStatusFilter, setRetryStatusFilter] = useState<string>("all");
  const [backfillResult, setBackfillResult] = useState<AccountingBackfillResult | null>(null);

  // Queries
  const rolesQuery = useQuery({
    queryKey: ["accounting", "roles"],
    queryFn: () => accountingApi.listRoles(),
  });

  const accountsQuery = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => accountingApi.listAccounts(),
  });

  const retryQuery = useQuery({
    queryKey: ["accounting", "retry", retryStatusFilter],
    queryFn: () => accountingApi.listRetryEvents(retryStatusFilter === "all" ? undefined : retryStatusFilter),
  });

  const roleMappings = rolesQuery.data ?? [];
  const accounts = accountsQuery.data ?? [];
  const retryEvents = retryQuery.data ?? [];

  // Leaf accounts for role assignment
  const leafAccounts = accounts.filter(
    (a) => a.status === "active" && (a.level === "subsidiary" || a.level === "detail"),
  );

  // Role assignments local state
  const [selectedRoleAccounts, setSelectedRoleAccounts] = useState<Record<string, string>>({});

  // Action: Set Role
  const setRoleAction = useAction(
    async ({ role, accountId }: { role: string; accountId: string }) => {
      await accountingApi.setRole(role, accountId);
    },
    [
      ["accounting", "roles"],
      ["accounting", "accounts"],
    ],
  );

  // Action: Retry Event
  const retryAction = useAction(
    async (id: string) => {
      await accountingApi.retryEvent(id);
    },
    [
      ["accounting", "retry"],
      ["accounting", "vouchers"],
    ],
  );

  // Action: Backfill
  const backfillAction = useAction(async () => {
    const res = await accountingApi.backfill();
    setBackfillResult(res);
  }, [
    ["accounting", "vouchers"],
    ["accounting", "reports"],
    ["accounting", "retry"],
  ]);

  return (
    <div className="stack" style={{ gap: "var(--s5)" }}>
      {/* 1. Account Roles */}
      <Panel title="نقش‌های استاندارد حسابداری (اتوماسیون مالی)">
        <div className="stack" style={{ gap: "var(--s4)" }}>
          <p className="text-secondary text-sm">
            این نگاشت‌ها مشخص می‌کنند که هنگام صدور خودکار اسناد حاصل از فاکتور، دریافت، چک و اعلامیه‌های
            مالی، هر بخش به کدام حساب معین منتسب گردد.
          </p>

          <ErrorLine error={setRoleAction.error} />

          {rolesQuery.isLoading ? (
            <div className="p-4 text-secondary">در حال بارگذاری نگاشت‌ها...</div>
          ) : (
            <div className="table-wrap">
              <table className="table" dir="rtl">
                <thead>
                  <tr>
                    <th style={{ width: "220px" }}>نقش سیستمی</th>
                    <th>حساب معین منتسب</th>
                    <th style={{ width: "120px", textAlign: "left" }}>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(ROLE_LABELS).map(([roleKey, roleLabel]) => {
                    const currentMapping = roleMappings.find((m) => m.role === roleKey);
                    const currentVal = selectedRoleAccounts[roleKey] ?? currentMapping?.accountId ?? "";

                    return (
                      <tr key={roleKey}>
                        <td className="bold">{roleLabel}</td>
                        <td>
                          <select
                            className="select"
                            value={currentVal}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSelectedRoleAccounts((prev) => ({ ...prev, [roleKey]: val }));
                            }}
                          >
                            <option value="">-- انتخاب حساب معین --</option>
                            {leafAccounts.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.code} — {a.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ textAlign: "left" }}>
                          <Button
                            variant="primary"
                            onClick={() => {
                              if (currentVal) {
                                setRoleAction.run({ role: roleKey, accountId: currentVal });
                              }
                            }}
                            disabled={
                              setRoleAction.busy || !currentVal || currentVal === currentMapping?.accountId
                            }
                          >
                            ذخیره
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      {/* 2. Backfill */}
      <Panel title="ثبت اسناد گذشته‌نگر (Backfill)">
        <div className="stack" style={{ gap: "var(--s3)" }}>
          <p className="text-secondary text-sm">
            تمامی فاکتورها، اعلامیه‌های بستانکار/بدهکار و پرداخت‌های پیشین که هنوز سندی برای آن‌ها در دفاتر
            ثبت نشده است، بررسی و اسناد متناظر صادر خواهند شد.
          </p>

          <ErrorLine error={backfillAction.error} />

          <div className="row items-center" style={{ gap: "var(--s3)" }}>
            <Button
              variant="primary"
              onClick={() => {
                const ok = window.confirm("آیا مایل به اجرای فرآیند ثبت اسناد گذشته‌نگر هستید؟");
                if (ok) backfillAction.run(undefined);
              }}
              disabled={backfillAction.busy}
            >
              {backfillAction.busy ? "در حال اجرای ثبت اسناد گذشته‌نگر..." : "اجرای ثبت گذشته‌نگر اسناد"}
            </Button>
          </div>

          {backfillResult && (
            <Card>
              <div className="stack" style={{ gap: "var(--s2)" }}>
                <span className="bold text-sm" style={{ color: "var(--color-success, #2e7d32)" }}>
                  نتیجه اجرای گذشته‌نگر:
                </span>
                <div className="row wrap text-sm" style={{ gap: "var(--s4)" }}>
                  <span>فاکتورها: {faDigits(backfillResult.invoicesCount)}</span>
                  <span>پرداخت‌ها: {faDigits(backfillResult.paymentsCount)}</span>
                  <span>اسناد ثبت‌شده: {faDigits(backfillResult.vouchersPosted)}</span>
                  <span>نادیده‌گرفته‌شده (قبلاً ثبت‌شده): {faDigits(backfillResult.vouchersSkipped)}</span>
                  {backfillResult.failedCount > 0 && (
                    <span style={{ color: "var(--color-danger, #d32f2f)" }}>
                      خطاها: {faDigits(backfillResult.failedCount)}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>
      </Panel>

      {/* 3. Retry Queue */}
      <Panel title="صف خطاهای صدور سند و تلاش دوباره">
        <div className="stack" style={{ gap: "var(--s4)" }}>
          <div className="row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
            <p className="text-secondary text-sm">
              رویدادهای مالی که به دلیل عدم نگاشت نقش یا خطای اعتبارسنجی ثبت نشده‌اند در این صف نگهداری
              می‌شوند.
            </p>

            <div className="field">
              <label htmlFor="retry-status-filter">فیلتر وضعیت</label>
              <select
                id="retry-status-filter"
                className="select"
                value={retryStatusFilter}
                onChange={(e) => {
                  setRetryStatusFilter(e.target.value);
                }}
                style={{ width: "auto" }}
              >
                <option value="all">همه وضعیت‌ها</option>
                <option value="pending">در انتظار (Pending)</option>
                <option value="failed">ناموفق (Failed)</option>
                <option value="resolved">حل‌شده (Resolved)</option>
              </select>
            </div>
          </div>

          <ErrorLine error={retryAction.error} />

          {retryQuery.isLoading ? (
            <div className="p-4 text-secondary">در حال بارگذاری صف خطاها...</div>
          ) : retryEvents.length === 0 ? (
            <Card>
              <div className="center p-6 text-secondary">موردی در صف تلاش مجدد وجود ندارد.</div>
            </Card>
          ) : (
            <div className="table-wrap">
              <table className="table" dir="rtl">
                <thead>
                  <tr>
                    <th style={{ width: "130px" }}>زمان رویداد</th>
                    <th style={{ width: "180px" }}>نوع رویداد</th>
                    <th>پیام خطا</th>
                    <th style={{ width: "90px" }}>تلاش‌ها</th>
                    <th style={{ width: "100px" }}>وضعیت</th>
                    <th style={{ width: "120px", textAlign: "left" }}>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {retryEvents.map((ev) => (
                    <tr key={ev.id}>
                      <td className="text-sm">{formatTime(ev.createdAt)}</td>
                      <td className="bold text-sm">{ev.eventType}</td>
                      <td className="text-sm" style={{ color: "var(--color-danger, #d32f2f)" }}>
                        {ev.errorMessage}
                      </td>
                      <td className="center">{faDigits(ev.attempts)}</td>
                      <td>
                        <Chip
                          tone={ev.status === "resolved" ? "ok" : ev.status === "pending" ? "blue" : "coral"}
                        >
                          {ev.status === "resolved"
                            ? "حل شده"
                            : ev.status === "pending"
                              ? "در انتظار"
                              : "ناموفق"}
                        </Chip>
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {ev.status !== "resolved" && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              retryAction.run(ev.id);
                            }}
                            disabled={retryAction.busy}
                          >
                            تلاش دوباره
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
